/**
 * Command palette registry context (Slice E4).
 *
 * Separate from the provider component so hooks/types can be imported
 * without tripping `react-refresh/only-export-components`.
 */
import { createContext, useContext, useEffect, useRef } from 'react';

export interface CommandPaletteItem {
  readonly id: string;
  readonly label: string;
  readonly keywords?: ReadonlyArray<string>;
  /** Optional keyboard-shortcut hint shown right-aligned (e.g. `?`, `A`). */
  readonly shortcut?: string;
  readonly onSelect: () => void;
}

export interface CommandPaletteSection {
  readonly id: string;
  readonly heading: string;
  readonly items: ReadonlyArray<CommandPaletteItem>;
  /**
   * Sort weight (ascending). Lower sorts earlier, so in-view action sections
   * appear before bulkier ones. Sections without an order default to 0.
   */
  readonly order?: number;
}

export interface CommandPaletteRegistry {
  readonly sections: ReadonlyArray<CommandPaletteSection>;
  readonly registerSection: (section: CommandPaletteSection) => () => void;
}

const noopRegistry: CommandPaletteRegistry = {
  sections: [],
  registerSection: () => () => undefined,
};

export const CommandPaletteRegistryCtx = createContext(noopRegistry);

export function useCommandPaletteSections(): ReadonlyArray<CommandPaletteSection> {
  return useContext(CommandPaletteRegistryCtx).sections;
}

/**
 * Register a section of contextual commands with the palette while this
 * component is mounted. Pass `null` to skip registration (e.g. while
 * underlying data is still loading). The caller is expected to memoise
 * `section` so identity is stable when nothing has changed.
 */
export function useRegisterCommandPaletteSection(section: CommandPaletteSection | null): void {
  const { registerSection } = useContext(CommandPaletteRegistryCtx);
  const sectionRef = useRef(section);

  useEffect(() => {
    sectionRef.current = section;
    if (section === null) return undefined;
    return registerSection(section);
  }, [section, registerSection]);
}
